import {
  Combobox,
  ComboboxItem,
  ComboboxList,
  ComboboxProvider,
  useComboboxStore
} from '@ariakit/react/combobox';
import * as RadixSelect from '@radix-ui/react-select';
import { startTransition, useEffect, useMemo, useState } from 'react';
import { CheckIcon, ChevronUpDownIcon, SearchIcon } from './icons';
import Styles from './searchselect.module.scss';

interface SelectData {
  data?: any;
  onChange?: (value: string) => void;
  label?: string;
  selectValue?: string;
  disabled?: boolean;
  placeholder?: string;
  width?: string;
  searchPlaceholder?: string;
}

export default function SearchSelect({
  data,
  onChange,
  label,
  placeholder,
  selectValue,
  disabled = false,
  searchPlaceholder,
  width
}: SelectData) {
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>(
    selectValue === label ? '' : selectValue || ''
  );
  const [searchValue, setSearchValue] = useState('');

  // Create combobox store
  const combobox = useComboboxStore({
    setValue: (value: string) => {
      startTransition(() => {
        setSearchValue(value);
      });
    }
  });

  const matches = useMemo(() => {
    if (!searchValue) {
      return data;
    }

    const filteredMatches = data.filter((item: any) =>
      item.label.toLowerCase().includes(searchValue.toLowerCase())
    );

    // If no matches found, add the "No data found" item
    if (filteredMatches.length === 0) {
      filteredMatches.push({
        value: 'no-data-found',
        label: 'No data found !',
        disabled: true
      });
    }

    const selectedData = data.find((data: any) => data.value === value);
    if (selectedData && !filteredMatches.includes(selectedData)) {
      filteredMatches.push(selectedData);
    }

    return filteredMatches;
  }, [searchValue, value, data]);

  useEffect(() => {
    selectValue && setValue(selectValue !== label ? selectValue : '');
  }, [selectValue, label]);

  useEffect(() => {
    setSearchValue('');
  }, [open]);

  return (
    <RadixSelect.Root
      value={value}
      onValueChange={(value: string) => {
        setValue(value);
        onChange?.(value);
      }}
      open={open}
      disabled={disabled}
      onOpenChange={setOpen}
    >
      <ComboboxProvider>
        <div style={{ maxWidth: '500px' }}>
          <div
            style={{
              fontSize: '60%',
              position: 'relative',
              marginTop: '-9px',
              padding: '2px',
              background: 'white',
              borderRadius: '6px',
              height: '8.5px',
              color: disabled ? 'gray' : 'black'
              // zIndex: 10
            }}
          >
            {placeholder}
          </div>
          <RadixSelect.Trigger
            aria-label="Language"
            className={Styles.select}
            style={{
              opacity: disabled ? 0.6 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
              width: 'fit-content',
              fontSize: '100%'
            }}
            onKeyDown={e => {
              if (e.key === ' ') {
                e.preventDefault();
              }
            }}
          >
            <RadixSelect.Value
              placeholder={value === '' ? label : value}
              className="text-[0.9] max-w-[10vw] text-clip text-nowrap"
            />
            <RadixSelect.Icon className={Styles.selectIcon}>
              <ChevronUpDownIcon />
            </RadixSelect.Icon>
          </RadixSelect.Trigger>
        </div>
        <RadixSelect.Content
          role="dialog"
          aria-label="Languages"
          position="popper"
          className={Styles.popover}
          sideOffset={4}
          alignOffset={-16}
        >
          <div className={Styles.comboboxWrapper}>
            <div className={Styles.comboboxIcon}>
              <SearchIcon />
            </div>
            <input
              className={Styles.combobox}
              placeholder={searchPlaceholder || label}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={e => {
                // Allow space inside the search input
                e.stopPropagation();
              }}
            />
          </div>
          <div className={Styles.listbox}>
            {matches.map(
              ({
                label,
                value,
                disabled
              }: {
                label: string;
                value: string;
                disabled?: boolean;
              }) => (
                <RadixSelect.Item
                  key={value}
                  value={value}
                  asChild
                  disabled={disabled}
                  className={Styles.item}
                >
                  <ComboboxItem store={combobox}>
                    <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
                    <RadixSelect.ItemIndicator className={Styles.itemIndicator}>
                      <CheckIcon />
                    </RadixSelect.ItemIndicator>
                  </ComboboxItem>
                </RadixSelect.Item>
              )
            )}
          </div>
        </RadixSelect.Content>
      </ComboboxProvider>
    </RadixSelect.Root>
  );
}
